/**
 * Public "Contact us" page — served at `/contact`.
 *
 * Content is fetched from the shared `landing_content` singleton via
 * `GET /landing/content` (no auth required). On any failure or for older
 * rows missing the `contactPage` blob, we fall back to
 * `DEFAULT_LANDING_CONTENT.contactPage`.
 */
import {
  DEFAULT_LANDING_CONTENT,
  type ContactOffice,
  type ContactSocialLink,
  type LandingContentView,
} from '@peerless/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ExternalLink, Globe, LogIn, Mail, MapPin, Phone } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Facebook, Instagram, Linkedin, Twitter } from '../../components/brand-glyphs';
import { useAuth } from '../../hooks/useAuth';
import { apiRequest } from '../../lib/api-client';
import { cn } from '../../lib/cn';


const DEFAULT_CONTACT = DEFAULT_LANDING_CONTENT.contactPage!;

const SOCIAL_ICONS: Record<ContactSocialLink['platform'], LucideIcon> = {
  twitter: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
};

export function ContactPage(): JSX.Element {
  const { user } = useAuth();
  const contentQuery = useQuery({
    queryKey: ['landing', 'content'],
    queryFn: () => apiRequest<LandingContentView>('/landing/content'),
    staleTime: 60_000,
  });
  const cp = contentQuery.data?.content.contactPage ?? DEFAULT_CONTACT;

  return (
    <div className="min-h-screen bg-paper text-ink antialiased">
      <PublicNav user={user} />

      <main className="mx-auto max-w-5xl px-6 py-16 sm:py-24">
        <header className="text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
            {cp.eyebrow}
          </p>
          <h1 className="mt-6 text-4xl font-semibold tracking-tight text-ink sm:text-5xl">
            {cp.title}
          </h1>
          {cp.subtitle && (
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-ink-muted">
              {cp.subtitle}
            </p>
          )}
        </header>

        {/* Quick contact strip */}
        <section className="mt-12 grid gap-4 sm:grid-cols-2">
          <a
            href={`mailto:${cp.email}`}
            className="group flex items-center gap-4 rounded border border-rule bg-paper p-5 transition hover:border-accent/40 hover:shadow-sm"
          >
            <div className="grid size-11 place-items-center rounded-tight bg-accent/10 text-accent">
              <Mail className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-tight text-ink-muted">Email</p>
              <p className="truncate text-sm font-semibold text-ink">{cp.email}</p>
            </div>
            <ExternalLink className="size-4 shrink-0 text-ink-muted transition group-hover:text-accent" />
          </a>
          <a
            href={cp.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 rounded border border-rule bg-paper p-5 transition hover:border-accent/40 hover:shadow-sm"
          >
            <div className="grid size-11 place-items-center rounded-tight bg-accent/10 text-accent">
              <Globe className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-tight text-ink-muted">Website</p>
              <p className="truncate text-sm font-semibold text-ink">
                {cp.websiteUrl.replace(/^https?:\/\//, '')}
              </p>
            </div>
            <ExternalLink className="size-4 shrink-0 text-ink-muted transition group-hover:text-accent" />
          </a>
        </section>

        {/* Offices */}
        {cp.offices.length > 0 && (
          <section className="mt-16">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
              {cp.officesSubtitle}
            </p>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight text-ink">
              {cp.officesTitle}
            </h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {cp.offices.map((office, idx) => (
                <OfficeCard key={idx} office={office} />
              ))}
            </div>
          </section>
        )}

        {/* Social */}
        {cp.socialLinks.length > 0 && (
          <section className="mt-16 rounded border border-rule bg-paper p-8 sm:p-10">
            <div className="grid gap-8 md:grid-cols-[1fr_2fr]">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
                  {cp.socialEyebrow}
                </p>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight text-ink">
                  {cp.socialHandle}
                </h2>
                {cp.socialBody && (
                  <p className="mt-3 text-sm leading-relaxed text-ink-muted">{cp.socialBody}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {cp.socialLinks.map((link, idx) => (
                  <SocialButton
                    key={idx}
                    href={link.url}
                    icon={SOCIAL_ICONS[link.platform]}
                    label={link.label}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="mt-16 rounded border border-rule bg-paper p-8 text-center sm:p-12">
          <div className="mx-auto flex max-w-2xl flex-col items-center gap-4">
            <h2 className="text-2xl font-semibold tracking-tight text-ink">{cp.ctaTitle}</h2>
            {cp.ctaBody && <p className="text-sm text-ink-muted">{cp.ctaBody}</p>}
            <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-md bg-accent px-5 py-2.5 text-sm font-medium text-accent-fg transition hover:opacity-90"
              >
                Sign in to AI Portal
                <ArrowRight className="size-4" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 rounded-md border border-rule bg-paper-soft px-5 py-2.5 text-sm font-medium text-ink transition hover:border-accent"
              >
                About Peerless
              </Link>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small per-page components — kept local so the public surfaces stay
// independent of the authenticated shell.
// ---------------------------------------------------------------------------

function OfficeCard({ office }: { office: ContactOffice }): JSX.Element {
  return (
    <article className="rounded border border-rule bg-paper p-5 transition hover:border-accent/40 hover:shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl leading-none" aria-hidden="true">
            {office.flag}
          </span>
          <h3 className="text-base font-semibold text-ink">{office.country}</h3>
        </div>
        {office.isHeadquarters && (
          <span className="inline-flex items-center rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
            HQ
          </span>
        )}
      </div>
      {office.address && (
        <div className="mt-4 flex items-start gap-2 text-sm text-ink-muted">
          <MapPin className="mt-0.5 size-4 shrink-0 text-ink-muted" />
          <span>{office.address}</span>
        </div>
      )}
      <a
        href={`tel:${office.phoneHref}`}
        className="mt-3 flex items-center gap-2 text-sm text-ink transition hover:text-accent"
      >
        <Phone className="size-4 shrink-0 text-ink-muted" />
        <span className="font-mono">{office.phone}</span>
      </a>
    </article>
  );
}

function SocialButton({
  href,
  icon: Icon,
  label,
}: {
  href: string;
  icon: LucideIcon;
  label: string;
}): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center gap-2 rounded-tight border border-rule bg-paper-soft px-3 py-2 text-xs font-medium text-ink-muted transition hover:border-accent hover:text-accent"
      aria-label={label}
    >
      <Icon className="size-4" />
      <span>{label}</span>
    </a>
  );
}

function PublicNav({ user }: { user: { id: string; role: string } | null }): JSX.Element {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const dashboardHref = user?.role === 'admin' ? '/admin/users' : '/chat';

  return (
    <header
      className={cn(
        'sticky top-0 z-30 transition-all duration-300',
        scrolled
          ? 'border-b border-rule/60 bg-paper/85 backdrop-blur-md shadow-sm supports-[backdrop-filter]:bg-paper/65'
          : 'border-b border-transparent bg-transparent',
      )}
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-6">
        <Link to="/" className="flex items-center gap-2.5 transition hover:opacity-90">
          <img
            src="/peerless-logo.png"
            alt="Peerless"
            className="h-7 w-auto select-none invert dark:invert-0 dark:drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]"
            draggable={false}
          />
          <span className="hidden text-sm font-semibold tracking-tight text-ink sm:inline">
            AI Portal
          </span>
        </Link>
        <nav className="hidden items-center gap-6 text-sm text-ink-muted md:flex">
          <Link to="/about" className="transition hover:text-ink">
            About
          </Link>
          <Link to="/contact" className="text-ink">
            Contact
          </Link>
          <a
            href="/wiki.html"
            target="_blank"
            rel="noopener noreferrer"
            className="transition hover:text-ink"
          >
            Wiki
          </a>
        </nav>
        <Link
          to={user ? dashboardHref : '/login'}
          className="inline-flex items-center gap-1.5 rounded-tight bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90"
        >
          {user ? <ArrowRight className="size-3.5" /> : <LogIn className="size-3.5" />}
          {user ? 'Open dashboard' : 'Sign in'}
        </Link>
      </div>
    </header>
  );
}

function PublicFooter(): JSX.Element {
  return (
    <footer className="border-t border-rule/60 bg-paper">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-10 text-xs text-ink-muted">
        <nav
          aria-label="Footer"
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 sm:justify-start"
        >
          <Link to="/about" className="transition hover:text-ink">
            About us
          </Link>
          <Link to="/contact" className="transition hover:text-ink">
            Contact us
          </Link>
          <a
            href="/wiki.html"
            target="_blank"
            rel="noopener noreferrer"
            className="transition hover:text-ink"
          >
            Wiki
          </a>
          <Link to="/login" className="transition hover:text-ink">
            Sign in
          </Link>
        </nav>
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <img
              src="/peerless-logo.png"
              alt="Peerless"
              className="h-5 w-auto opacity-80 select-none invert dark:invert-0 dark:drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]"
              draggable={false}
            />
            <span className="text-ink/80">AI Portal</span>
          </div>
          <p>© Peerless Technology — built for teams that ship.</p>
        </div>
      </div>
    </footer>
  );
}
